
import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { MatchData } from '@/components/team-details/types/selector-types';
import { Json } from '@/integrations/supabase/types';
import { toast } from 'sonner';

// Create a type that matches what Supabase returns
interface SupabaseSavedState {
  id: number;
  name: string;
  league: string | null;
  year: string | null;
  data: Json;
  created_at: string;
  country: string | null;
  display_order?: number | null;
}

export const useOddsMatches = (
  country: string | null,
  league: string | null,
  selectedYears: string[],
  team: string | null = null
) => {
  return useQuery({
    queryKey: ['odds-matches', country, league, selectedYears, team],
    queryFn: async () => {
      if (!country || !league || selectedYears.length === 0) {
        return [];
      }
      
      try {
        // Optimize the query by only selecting the fields we need
        const { data, error } = await supabase
          .from('saved_states')
          .select('year,data')
          .eq('country', country)
          .eq('league', league)
          .in('year', selectedYears);
        
        if (error) {
          toast.error('Failed to load data: ' + error.message);
          throw error;
        }
        
        if (!data || data.length === 0) {
          return [];
        }
        
        // Use more efficient array allocation and processing
        let allMatches: MatchData[] = [];
        
        // Cast the Supabase response to our SupabaseSavedState type
        for (const state of data as SupabaseSavedState[]) {
          if (state.data && Array.isArray(state.data)) {
            // Append matches directly without creating intermediary arrays
            allMatches = allMatches.concat(state.data as unknown as MatchData[]);
          }
        }
        
        // Filter by team if provided - do this after concatenation for better performance
        if (team) {
          return allMatches.filter(match => 
            match.HomeTeam === team || match.AwayTeam === team
          );
        }
        
        return allMatches;
      } catch (err) {
        console.error('Exception in data fetch:', err);
        toast.error('Failed to load data: ' + (err instanceof Error ? err.message : 'Unknown error'));
        return [];
      }
    },
    enabled: !!country && !!league && selectedYears.length > 0,
    staleTime: 15 * 60 * 1000, // Increase to 15 minutes
    gcTime: 60 * 60 * 1000,    // Increase to 60 minutes
    retry: 1,                  // Reduce retry attempts
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};
